import { Grid, LoadingOverlay, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { AsyncAutocomplete, TextInput } from '@components';
import { useDeclarant, useGoodLocation } from '@hooks/declarant';
import { TransitViewGeneralInfoFormValues } from './validateTransitViewGeneralInfoForm';

interface TransitLocationSectionProps {
  form: UseFormReturnType<TransitViewGeneralInfoFormValues>;
  editDisabled: boolean;
}

export const TransitLocationSection: React.FC<TransitLocationSectionProps> = ({ form, editDisabled }) => {
  const { data: declarant } = useDeclarant();
  const { data: { data: goodsLocations } = {}, isLoading } = useGoodLocation(declarant._id);
  const [unlocodes, setUnlocodes] = useState([]);
  const [qualifierOfIdentification, setQualifierOfIdentification] = useState('');

  const handleUNLocodeChange = (value) => {
    form.setFieldValue('Consignment.LocationOfGoods.UNLocode', value);

    const selectedGoodsLocation = goodsLocations.find((code) => code.unLocode === value);
    form.setFieldValue('Consignment.LocationOfGoods.typeOfLocation', selectedGoodsLocation?.type || '');
    form.setFieldValue('Consignment.LocationOfGoods.qualifierOfIdentification', selectedGoodsLocation?.qualifierOfIdentification || '');
    form.setFieldValue('Consignment.LocationOfGoods.additionalIdentifier', selectedGoodsLocation?.additionalIdentifier || '');
    form.setFieldValue('Consignment.LocationOfGoods.Address', {
      city: selectedGoodsLocation?.address?.city || '',
      country: selectedGoodsLocation?.address?.country || '',
      postcode: selectedGoodsLocation?.address?.postcode || '',
      streetAndNumber: selectedGoodsLocation?.address?.street || '',
    });
    form.setFieldValue('Consignment.LocationOfGoods.PostcodeAddress', {
      postcode: selectedGoodsLocation?.postcode?.postcode ?? '',
      country: selectedGoodsLocation?.postcode?.country ?? '',
      houseNumber: selectedGoodsLocation?.postcode?.houseNumber ?? '',
    });
  };

  useEffect(() => {
    if (goodsLocations?.length) {
      setUnlocodes(goodsLocations.map((loc) => ({ label: loc.unLocode, value: loc.unLocode })));
    }
  }, [goodsLocations]);

  useEffect(() => {
    setQualifierOfIdentification(form.values.Consignment.LocationOfGoods.qualifierOfIdentification);
  }, [form.values.Consignment.LocationOfGoods.qualifierOfIdentification]);

  if (!goodsLocations) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack mb="xl">
      <Title order={5} fw={600}>
        Location of Goods
      </Title>
      <Grid>
        <Grid.Col span={4}>
          <AsyncAutocomplete
            {...form.getInputProps('Consignment.LocationOfGoods.UNLocode')}
            label="UNLOCODE / Warehouse ID"
            fetchOptions={(search: string) => unlocodes.filter((code) => code.value.includes(search)) as any}
            onChange={(value) => handleUNLocodeChange(value)}
            disabled={editDisabled}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput {...form.getInputProps('Consignment.LocationOfGoods.typeOfLocation')} label="Type of location" disabled={true} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput {...form.getInputProps('Consignment.LocationOfGoods.qualifierOfIdentification')} label="Qualifier of identification" disabled={true} />
        </Grid.Col>
      </Grid>
      {qualifierOfIdentification !== 'T' && (
        <Grid>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.Address.streetAndNumber')} label="Street and Number" disabled={true} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.Address.postcode')} label="Postcode" disabled={true} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.Address.city')} label="City" disabled={true} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.Address.country')} label="Country" disabled={true} />
          </Grid.Col>
        </Grid>
      )}
      {qualifierOfIdentification === 'T' && (
        <Grid>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.PostcodeAddress.postcode')} label="Postcode" disabled={true} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.PostcodeAddress.houseNumber')} label="House No." disabled={true} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('Consignment.LocationOfGoods.PostcodeAddress.country')} label="Country" disabled={true} />
          </Grid.Col>
        </Grid>
      )}
    </Stack>
  );
};
